import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface SessionRecording {
  id: string;
  deviceId: string;
  sessionType: string;
  title: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  createdAt: string;
  /** Who opened the terminal session, when the viewer may read the activity log. */
  openedBy: string | null;
}

export interface SessionRecordingListResponse {
  recordings: SessionRecording[];
  total: number;
  page: number;
  pageSize: number;
}

export function useSessionRecordings(
  deviceId: string | undefined,
  page: number,
  pageSize: number,
) {
  const contextReady = useActiveContextReady();
  return useQuery<SessionRecordingListResponse>({
    queryKey: ['sessionRecordings', deviceId, page, pageSize],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<SessionRecordingListResponse>(
        `${config.backendApi}/api/devices/${deviceId}/session-recordings`,
        {
          withCredentials: true,
          params: { page, page_size: pageSize },
        }
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
    // Keep the previous page's rows visible while the next page loads, so
    // paging doesn't collapse the list into a skeleton on every click.
    placeholderData: keepPreviousData,
  });
}

export function useSessionRecordingData(deviceId: string | undefined, recordingId: string | null) {
  const contextReady = useActiveContextReady();
  return useQuery<string>({
    queryKey: ['sessionRecordingData', deviceId, recordingId],
    queryFn: async () => {
      if (!deviceId || !recordingId) throw new Error('Device and recording IDs are required');
      const response = await axios.get<string>(
        `${config.backendApi}/api/devices/${deviceId}/session-recordings/${recordingId}`,
        { withCredentials: true, responseType: 'text' }
      );
      return response.data;
    },
    enabled: !!deviceId && !!recordingId && contextReady,
    staleTime: Infinity, // Recording data never changes
  });
}
